/**
 * Mock data for the Knowledge Base page. There is no backend for this feature
 * yet — everything here is seed data + pure helper functions consumed by
 * `KnowledgeBasePage` and its section components. BDT prices and BD
 * F-commerce flavored catalog (kurti, saree, panjabi, mugs, phone cases…)
 * matching the target seller segment described in the product brief.
 */

/** Where a product's data originated. Drives badge styling via `sourceMeta.ts`. */
export type ProductSource = 'shopify' | 'woocommerce' | 'manual' | 'csv' | 'sheet';

export interface Product {
  id: string;
  title: string;
  /** Price in BDT (৳), stored as a plain number — formatting happens at render time. */
  price: number;
  description: string;
  /** Units in stock. 0 renders as "Out of stock". */
  stock: number;
  imageUrl: string;
  category: string;
  tags: string[];
  source: ProductSource;
  /** Whether this product is currently included in what the AI can answer from. */
  inKnowledgeBase: boolean;
  /** ISO timestamp — only present for auto-synced sources (shopify/woocommerce). */
  lastSynced?: string;
}

const KNOWN_SOURCES: ProductSource[] = ['shopify', 'woocommerce', 'manual', 'csv', 'sheet'];

/**
 * Coerce a raw product blob (from the shop config's free-form productsJson —
 * seeded catalogs, manual entries, CSV imports) into a full `Product` with safe
 * defaults. Prevents render crashes when fields like `source`/`title`/`stock`
 * are missing. Unknown sources fall back to `manual`; a missing stock becomes
 * NaN and renders as "In stock" (unknown) rather than "Out of stock".
 */
export function normalizeProduct(raw: any, index = 0): Product {
  const source: ProductSource = KNOWN_SOURCES.includes(raw?.source) ? raw.source : 'manual';
  const priceNum = typeof raw?.price === 'number' ? raw.price : Number(raw?.price);
  const title = String(raw?.title ?? raw?.name ?? 'Untitled product');
  return {
    id: String(raw?.id ?? `prod-${index}-${title.toLowerCase().replace(/\s+/g, '-').slice(0, 32)}`),
    title,
    price: Number.isFinite(priceNum) ? priceNum : 0,
    description: String(raw?.description ?? ''),
    stock: typeof raw?.stock === 'number' ? raw.stock : Number.NaN,
    imageUrl: String(raw?.imageUrl ?? raw?.image ?? ''),
    category: String(raw?.category ?? raw?.productType ?? 'Uncategorized'),
    tags: Array.isArray(raw?.tags) ? raw.tags : [],
    source,
    inKnowledgeBase: typeof raw?.inKnowledgeBase === 'boolean' ? raw.inKnowledgeBase : true,
    lastSynced: raw?.lastSynced,
  };
}

export interface FaqEntry {
  id: string;
  /** Short question or policy topic, e.g. "Delivery charge" or "Do you accept bKash?" */
  question: string;
  answer: string;
  inKnowledgeBase: boolean;
}

/** ISO timestamp for the most recent full re-index, used to derive the relative-time label in Section 1. */
export const LAST_REINDEX_AT = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();

export const seedProducts: Product[] = [
  {
    id: 'prod-1',
    title: 'Red Embroidered Kurti — Cotton',
    price: 1450,
    description: 'Hand-embroidered cotton kurti with mirror work, available in sizes M–XXL.',
    stock: 24,
    imageUrl: '/mock/kurti-red.jpg',
    category: "Women's Fashion",
    tags: ['kurti', 'cotton', 'embroidered'],
    source: 'shopify',
    inKnowledgeBase: true,
    lastSynced: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'prod-2',
    title: '4-Piece Ceramic Mug Set',
    price: 980,
    description: '',
    stock: 40,
    imageUrl: '/mock/mug-set.jpg',
    category: 'Home & Kitchen',
    tags: ['mug', 'ceramic', 'gift'],
    source: 'shopify',
    inKnowledgeBase: true,
    lastSynced: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'prod-3',
    title: 'Jamdani Saree — Royal Blue',
    price: 4200,
    description: '',
    stock: 6,
    imageUrl: '/mock/saree-blue.jpg',
    category: "Women's Fashion",
    tags: ['saree', 'jamdani', 'wedding'],
    source: 'shopify',
    inKnowledgeBase: true,
    lastSynced: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'prod-4',
    title: 'Cotton Panjabi — White',
    price: 1150,
    description: 'Slim-fit white panjabi, breathable cotton, perfect for Eid and Friday prayers.',
    stock: 0,
    imageUrl: '/mock/panjabi-white.jpg',
    category: "Men's Fashion",
    tags: ['panjabi', 'cotton', 'eid'],
    source: 'woocommerce',
    inKnowledgeBase: true,
    lastSynced: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'prod-5',
    title: 'Clear Silicone Phone Case (Universal)',
    price: 250,
    description: '',
    stock: 120,
    imageUrl: '/mock/phone-case.jpg',
    category: 'Accessories',
    tags: ['phone case', 'silicone'],
    source: 'woocommerce',
    inKnowledgeBase: false,
    lastSynced: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'prod-6',
    title: 'Handloom Cotton Scarf',
    price: 650,
    description: 'Lightweight handloom scarf, soft finish, 6 colour options.',
    stock: 18,
    imageUrl: '/mock/scarf.jpg',
    category: 'Accessories',
    tags: ['scarf', 'handloom'],
    source: 'manual',
    inKnowledgeBase: true,
  },
  {
    id: 'prod-7',
    title: 'Classic Leather Tote Bag — Black',
    price: 2300,
    description: '',
    stock: 5,
    imageUrl: '/mock/tote-black.jpg',
    category: 'Bags',
    tags: ['bag', 'leather', 'tote'],
    source: 'manual',
    inKnowledgeBase: true,
  },
  {
    id: 'prod-8',
    title: 'Nakshi Kantha Cushion Cover (Set of 2)',
    price: 890,
    description: '',
    stock: 15,
    imageUrl: '/mock/cushion-cover.jpg',
    category: 'Home & Kitchen',
    tags: ['cushion', 'nakshi kantha', 'home decor'],
    source: 'csv',
    inKnowledgeBase: true,
  },
  {
    id: 'prod-9',
    title: 'Genuine Leather Wallet — Brown',
    price: 780,
    description: 'RFID-blocking bifold wallet, 6 card slots.',
    stock: 32,
    imageUrl: '/mock/wallet-brown.jpg',
    category: 'Accessories',
    tags: ['wallet', 'leather'],
    source: 'csv',
    inKnowledgeBase: true,
  },
  {
    id: 'prod-10',
    title: 'Printed Cotton Panjabi — Navy',
    price: 1350,
    description: '',
    stock: 9,
    imageUrl: '/mock/panjabi-navy.jpg',
    category: "Men's Fashion",
    tags: ['panjabi', 'printed'],
    source: 'csv',
    inKnowledgeBase: false,
  },
  {
    id: 'prod-11',
    title: 'Wireless Earbuds — 20H Battery',
    price: 1650,
    description: 'Bluetooth 5.3, touch controls, comes with charging case.',
    stock: 22,
    imageUrl: '/mock/earbuds.jpg',
    category: 'Electronics',
    tags: ['earbuds', 'wireless', 'audio'],
    source: 'sheet',
    inKnowledgeBase: true,
  },
  {
    id: 'prod-12',
    title: 'Kids Cotton T-Shirt (3-pack)',
    price: 590,
    description: '',
    stock: 27,
    imageUrl: '/mock/kids-tshirt.jpg',
    category: 'Kids',
    tags: ['kids', 't-shirt', 'cotton'],
    source: 'sheet',
    inKnowledgeBase: true,
  },
  {
    id: 'prod-13',
    title: 'Stainless Steel Water Bottle — 1L',
    price: 480,
    description: 'Double-wall insulated, keeps drinks cold for 24 hours.',
    stock: 33,
    imageUrl: '/mock/water-bottle.jpg',
    category: 'Home & Kitchen',
    tags: ['bottle', 'steel', 'insulated'],
    source: 'sheet',
    inKnowledgeBase: false,
  },
];

export const seedFaqs: FaqEntry[] = [
  {
    id: 'faq-1',
    question: 'Delivery charge',
    answer: 'Inside Dhaka ৳60, outside Dhaka ৳120. Orders are delivered within 2–4 business days.',
    inKnowledgeBase: true,
  },
  {
    id: 'faq-2',
    question: 'Do you accept bKash or Nagad?',
    answer: 'Yes — we accept bKash, Nagad, and cash on delivery. Advance payment is required for orders above ৳5,000.',
    inKnowledgeBase: true,
  },
  {
    id: 'faq-3',
    question: 'Return & exchange policy',
    answer: '3-day easy return for unused items with tags intact. Exchanges are free; refunds take 5–7 business days.',
    inKnowledgeBase: true,
  },
  {
    id: 'faq-4',
    question: 'Sizing guide',
    answer: 'Kurti sizes run true to standard BD sizing (M = 38", L = 40", XL = 42"). Message us your bust/waist for a custom fit check.',
    inKnowledgeBase: true,
  },
  {
    id: 'faq-5',
    question: 'Business hours',
    answer: 'We reply to messages from 10am–10pm, 7 days a week. Orders placed after 8pm ship the next day.',
    inKnowledgeBase: true,
  },
  {
    id: 'faq-6',
    question: 'Do you ship outside Bangladesh?',
    answer: 'Not yet — we currently deliver within Bangladesh only via Pathao and Steadfast courier.',
    inKnowledgeBase: false,
  },
  {
    id: 'faq-7',
    question: 'Can I customize an order (color, size, embroidery)?',
    answer: 'Yes for most kurti and panjabi items — message us before ordering and we will confirm feasibility and price.',
    inKnowledgeBase: true,
  },
];

/** Number of catalog items missing a description — the "needs attention" coverage signal in Section 1. */
export function countProductsMissingDescriptions(products: Product[]): number {
  return products.filter((product) => product.description.trim().length === 0).length;
}

/** Total items (products + FAQ entries) currently feeding the AI's answers. */
export function countInKnowledgeBase(products: Product[], faqs: FaqEntry[]): number {
  return (
    products.filter((product) => product.inKnowledgeBase).length +
    faqs.filter((faq) => faq.inKnowledgeBase).length
  );
}

export function formatBdt(amount: number): string {
  return `৳${amount.toLocaleString('en-US')}`;
}
